import {
  type GetStaticPropsContext,
  type GetStaticPaths,
  type NextPage,
  type InferGetStaticPropsType,
} from "next";
import Link from "next/link";
import Image from "next/image";
import React from "react";
import CategoryTitle from "~/components/CategoryTitle";
import GridLoop from "~/components/GridLoop";
import Layout from "~/components/Layout";
import { usePagination } from "~/components/Pagination";
import Pagination from "~/components/Pagination/Pagination";
import {
  getSpecialiSlug,
  getNameSpeciali,
  type getNameSubSpecialiType,
  getParentSpeciali,
  getSubSpeciali,
  getDetailsSpeciali,
} from "~/lib/db/speciali";
import { type LoopPostReturnType } from "~/lib/db/loops";
import {
  getArticleByCategory,
  getPostsMetadata,
  getThumbnail,
} from "~/lib/db/posts";
import { ISSUES_TYPE, type ArticlePreviewType } from "~/types";
import { postExcerpt } from "~/utils";
import SpecialiComponent from "~/components/SpecialiComponent";
import Masonry from "react-masonry-css";

const DEFAULT_ITEMS_PER_PAGE = 18;
const breakpointColumnsObj = {
  default: 3,
  1280: 2,
  768: 1,
};
const Speciale: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  categoryName,
  category_slug,
  parent_category_slug,
  parent_category_name,
  articles,
  subCategory,
  getDetailsSpeciale,
}) => {
  const articlessShow = usePagination(articles, DEFAULT_ITEMS_PER_PAGE);
  articlessShow.map((el, index) => {
    articlessShow[index].meta.categories = Object.values(
      el.meta.categories
    ).filter(
      (category) =>
        category.slug != "speciale" &&
        category.taxonomy !== ISSUES_TYPE.speciali
    );
    articlessShow[index].meta.cat_heading =
      articlessShow[index]?.meta?.cat_heading?.slug != "speciale"
        ? articlessShow[index]?.meta?.cat_heading
        : null;
  });

  const items = articlessShow.map((el, index) => {
    return {
      id: index,
      articolo: {
        speciale: el,
      },
    };
  });
  return (
    <>
      <Layout title={categoryName}>
        {subCategory.length > 0 ? (
          <section className="mx-auto mt-0 max-w-box px-4">
            <div className="mx-auto mt-0 max-w-box py-10">
              <CategoryTitle
                titleName={parent_category_name}
                titleSlug={parent_category_slug}
              />
              {items && items.length > 0 && (
                <Masonry
                  breakpointCols={breakpointColumnsObj}
                  className="my-masonry-grid grid gap-4 md:gap-6"
                  columnClassName="my-masonry-grid_column grid gap-y-4 md:gap-y-6"
                >
                  {items.map((item, index) => (
                    <SpecialiComponent
                      key={index}
                      data={item}
                      tax={ISSUES_TYPE.speciali}
                    />
                  ))}
                </Masonry>
              )}
            </div>
          </section>
        ) : (
          <>
            {getDetailsSpeciale != null && getDetailsSpeciale[0]?.img && (
              <section className="mx-auto -mt-12 w-full bg-lightLightGray">
                <Image
                  src={getDetailsSpeciale[0].img}
                  width={1920}
                  height={1000}
                  sizes="100vw"
                  quality="65"
                  priority={true}
                  alt={getDetailsSpeciale[0].name}
                  className="aspect-video w-full object-cover mix-blend-multiply hover:opacity-80"
                />
              </section>
            )}
            <section className="mx-auto mt-0 max-w-box px-4 py-16 sm:py-28">
              <h3 className="mb-4 text-lg font-medium sm:mb-9 sm:text-xl">
                Speciale
              </h3>
              {getDetailsSpeciale != null && (
                <>
                  <span className="mb-12 inline-block text-lg uppercase sm:mb-20 sm:text-xl">
                    <Link
                      href={`/n/${
                        getDetailsSpeciale[0].numeroSlug &&
                        getDetailsSpeciale[0].numeroSlug
                      }`}
                      className="font-bold text-orange"
                    >
                      {getDetailsSpeciale[0].numeroName &&
                        `BS ${
                          getDetailsSpeciale[0].numeroName &&
                          getDetailsSpeciale[0].numeroName
                        }`}
                    </Link>

                    <span className="font-light">
                      {getDetailsSpeciale[0].numberDescription &&
                        ` / ${getDetailsSpeciale[0].numberDescription}`}
                    </span>
                  </span>
                  <h1 className="xl:text-leading-none mb-10 text-4xl font-light sm:mb-16 sm:text-5xl md:text-6xl xl:text-[68px]">
                    {getDetailsSpeciale[0].name}
                  </h1>
                  <p className="mb-20 text-base font-medium sm:mb-40 sm:text-lg ">
                    {getDetailsSpeciale[0].description
                      ? getDetailsSpeciale[0].description
                      : "Speciale monografico del mese"}
                  </p>
                </>
              )}
              <div className="article archive mx-auto">
                <h2 className="mb-14 text-4xl font-medium sm:mb-24">
                  Gli articoli dello speciale
                </h2>
                <GridLoop
                  gridloop={articlessShow as ArticlePreviewType[]}
                  categoria={"speciale"}
                  imageShow={true}
                />
              </div>
            </section>
            <section className="w-full text-balance bg-black px-4 py-24 text-center">
              <Link
                href={`${category_slug}/all`}
                className="text-2xl font-medium uppercase text-orange"
              >
                Apri la pagina dello speciale completo
              </Link>
            </section>
          </>
        )}

        <div
          className={`mx-auto mt-6 max-w-box px-4 text-center ${
            articles.length > DEFAULT_ITEMS_PER_PAGE ? "" : "hidden"
          }`}
        >
          <Pagination
            totalResults={articles.length}
            itemsPerPage={DEFAULT_ITEMS_PER_PAGE}
          />
        </div>
      </Layout>
    </>
  );
};

export default Speciale;

export const getStaticPaths: GetStaticPaths<{
  speciali: string;
}> = async () => {
  const specialiSlug = await getSpecialiSlug(ISSUES_TYPE.speciali);

  return {
    paths: specialiSlug.map((speciali) => ({
      params: { speciali: speciali.slug },
    })), //indicates that no page needs be created at build time
    fallback: "blocking", //indicates the type of fallback
  };
};

export const getStaticProps = async ({ params }: GetStaticPropsContext) => {
  if (!params || params.speciali == "speciali") return { notFound: true };

  try {
    const speciali = await getNameSpeciali(params.speciali as string);
    if (!speciali) return { notFound: true };
    const [parent, subCategory, articleByCategory] = await Promise.all([
      getParentSpeciali(speciali.parent),
      getSubSpeciali(speciali.term_id),
      getArticleByCategory(speciali.term_id),
    ]);

    const getDetailsSpeciale = await getDetailsSpeciali(
      params.speciali as string
    );

    const articlesId = articleByCategory.map((el) => el.ID);

    const [metaData, thumbnail] = await Promise.all([
      articlesId.length > 0
        ? getPostsMetadata(articlesId)
        : Promise.resolve([]),
      articlesId.length > 0 ? getThumbnail(articlesId) : Promise.resolve([]),
    ]);

    const mapPostsForClient = (
      posts: LoopPostReturnType[]
    ): ArticlePreviewType[] =>
      posts.map(
        ({
          ID,
          post_title,
          post_name,
          post_excerpt,
          post_content,
          post_date,
        }) => ({
          post_name,
          post_title,
          post_excerpt:
            post_excerpt?.trim() != ""
              ? postExcerpt(post_excerpt)
              : postExcerpt(
                  post_content
                    ?.replace(/<\/?[^>]+(>|$)/g, "")
                    ?.replace(/\[\/{0,1}et_pb_.*\]/g, "")
                ),
          meta: metaData[Number(ID)] != null ? metaData[Number(ID)] : null,
          thumbnail:
            thumbnail[Number(ID)]?.thumbnail != null
              ? thumbnail[Number(ID)]?.thumbnail
              : null,
          alt:
            thumbnail[Number(ID)]?.alt != null
              ? thumbnail[Number(ID)]?.alt
              : null,
          post_date: post_date.toISOString(),
        })
      );
    const mapSpecialiForClient = (
      posts: getNameSubSpecialiType[]
    ): getNameSubSpecialiType[] =>
      posts.map(({ slug, name, description }) => ({
        slug,
        name,
        description,
      }));

    return {
      props: {
        categoryName: speciali.name,
        category_slug: params.speciali,
        parent_category_slug: parent?.slug || params.speciali,
        parent_category_name:
          parent?.name.replace("&amp;", " & ") ||
          speciali.name.replace("&amp;", " & "),
        subCategory: mapSpecialiForClient(subCategory),
        articles: mapPostsForClient(articleByCategory),
        getDetailsSpeciale:
          getDetailsSpeciale != null ? getDetailsSpeciale : null,
      },
      revalidate: 20 * 60,
    };
  } catch (error) {
    console.log("Error fetching static props for", params.speciali, error);
    return { notFound: true };
  }
};
